import { Injectable, signal, computed, effect } from '@angular/core';
import { StorageService } from './storage/storage.service';
import { PlatformService } from './platform.service';
import { UserLocalization } from '@shared/models/locale/locale.models';

// Import translations directly - this is synchronous and works immediately (i18n is under src, not in publicDir)
import enTranslations from '../../../i18n/en_US.json';
import ukTranslations from '../../../i18n/uk_UA.json';
import ruTranslations from '../../../i18n/ru_UA.json';

type TranslationObject = Record<string, any>;

@Injectable({
  providedIn: 'root',
})
export class TranslationService {
  // Load translations synchronously from imports
  private readonly translations: Record<string, TranslationObject> = {
    en_US: enTranslations as TranslationObject,
    uk_UA: ukTranslations as TranslationObject,
    ru_UA: ruTranslations as TranslationObject,
  };

  private readonly currentLocale = signal<UserLocalization>(UserLocalization.uk_UA);

  public readonly currentLocaleSignal = computed(() => this.currentLocale());

  constructor(
    private storageService: StorageService,
    private platformService: PlatformService,
  ) {
    // Initialize locale from storage
    if (this.platformService.isBrowser()) {
      const storedLocale = this.storageService.getItem('locale') as UserLocalization;
      if (storedLocale && this.isSupportedLocale(storedLocale)) {
        this.currentLocale.set(storedLocale);
      }
    }

    // Watch for locale changes in storage (cross-tab communication)
    if (this.platformService.isBrowser()) {
      effect(() => {
        const locale = this.currentLocale();
        this.storageService.setItem('locale', locale);
        // Dispatch custom event for cross-tab communication
        window.dispatchEvent(
          new CustomEvent('locale-changed', {
            detail: { locale },
          }),
        );
      });

      // Listen for locale changes from other tabs
      window.addEventListener('storage', (event) => {
        if (event.key === 'locale' && event.newValue) {
          const newLocale = event.newValue as UserLocalization;
          if (this.isSupportedLocale(newLocale)) {
            this.currentLocale.set(newLocale);
          }
        }
      });

      // Listen for custom locale-changed events
      window.addEventListener('locale-changed', ((event: CustomEvent) => {
        if (event.detail?.locale && this.isSupportedLocale(event.detail.locale)) {
          this.currentLocale.set(event.detail.locale);
        }
      }) as EventListener);
    }
  }


  public getSupportedLocales(): UserLocalization[] {
    return [UserLocalization.en_US, UserLocalization.ru_UA, UserLocalization.uk_UA];
  }

  public getCurrentLocale(): UserLocalization {
    return this.currentLocale();
  }

  public setLocale(locale: UserLocalization): void {
    if (this.isSupportedLocale(locale)) {
      this.currentLocale.set(locale);
      // Trigger change detection by dispatching a custom event
      if (this.platformService.isBrowser()) {
        window.dispatchEvent(new Event('locale-changed'));
      }
    }
  }

  /**
   * @param params Values for `{{name}}` placeholders in the translated string.
   *
   * Interpolation exists because some copy is assembled from data the server holds — a product
   * title, an order number, a count — and pre-composing the sentence server-side is what made the
   * analytics module untranslatable in the first place. Passing values in keeps word order a
   * property of the language rather than of the backend.
   */
  public instant(key: string | null | undefined, params?: Record<string, string | number>): string {
    if (!key || typeof key !== 'string') {
      return '';
    }
    const locale = this.currentLocale();
    const translation = this.getTranslation(key, locale);
    return this.interpolate(translation || key, params);
  }

  public get(key: string): string {
    return this.instant(key);
  }

  /**
   * Substitute `{{name}}` placeholders.
   *
   * A placeholder with no matching value is left standing rather than blanked: a visible `{{title}}`
   * is a bug report, an empty gap is a mystery. Values are substituted literally — Angular escapes
   * on render, and these strings are bound as text, never as HTML.
   */
  private interpolate(text: string, params?: Record<string, string | number>): string {
    if (!params) {
      return text;
    }
    return text.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
      params[name] === undefined || params[name] === null ? match : String(params[name]),
    );
  }

  private getTranslation(key: string, locale: UserLocalization): string | null {
    const translations = this.translations[locale] || this.translations[UserLocalization.uk_UA];
    const keys = key.split('.');

    let value: any = translations;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to uk_UA if key not found
        if (locale !== UserLocalization.uk_UA && this.translations[UserLocalization.uk_UA]) {
          const fallbackTranslations = this.translations[UserLocalization.uk_UA];
          let fallbackValue: any = fallbackTranslations;
          for (const fallbackKey of keys) {
            if (fallbackValue && typeof fallbackValue === 'object' && fallbackKey in fallbackValue) {
              fallbackValue = fallbackValue[fallbackKey];
            } else {
              return null;
            }
          }
          return typeof fallbackValue === 'string' ? fallbackValue : null;
        }
        return null;
      }
    }

    return typeof value === 'string' ? value : null;
  }

  private isSupportedLocale(locale: string): locale is UserLocalization {
    return Object.values(UserLocalization).includes(locale as UserLocalization);
  }
}
